import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import { BILLING_TYPE } from "../../../invoice/InvoiceBillingType"
import { BILLING_METHOD } from "../../../product/Types"
import { PREFERENCE_TYPE } from "../../constants"

export enum SubscriptionColumnIds {
    IDENTIFIER = "identifier",
    LOCATION_NAME = "locationName",
    CONTACT = "contact",
    LAST_INVOICE_DATE = "lastDueDate",
    NEXT_INVOICE_DATE = "nextDueDate",
    STATUS = "status",
    TOTAL_AMOUNT = "totalAmount",
    EFFECTIVE_DATE = "effectiveDate",
    PO_NUMBER = "poNumber",
    CUSTOMER_TYPE = "customerType",
    CREATED_AT_DATE = "createdAt",
    UPDATED_AT_DATE = "updatedAt",
    CONTACT_NAME = "contactName",
    CONTACT_FIRST_NAME = "contactFirstName",
    CONTACT_LAST_NAME = "contactLastName",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType",
    CONTACT_PHONE_NUMBER = "contactPhoneNumber",
    CONTACT_EMAIL = "contactEmail",
    CURRENCY = "currency",
    TAX_ID = "taxId",
    PAYMENT_TERM_ID = "paymentTermId",
    PRODUCTS_TITLE = "productsTitle",
    PRODUCTS_SKU = "productsSku",
    PRODUCTS_LABELS = "productsLabels",
    CAPTURE_PAYMENT = "capturePayment",
    SEND_EMAIL = "sendEmail",
    NEXT_CREATION_DATE = "nextCreationDate",
    END_DATE = "endDate",
    START_DATE = "startDate",
    INTERVAL_TYPE = "intervalType",
    CREATED_INVOICE_COUNT = "createdInvoiceCount",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: SubscriptionColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/invoices/{id}/edit",
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: SubscriptionColumnIds.LOCATION_NAME,
        sortId: "locationNameSort",
        dataType: DataType.LOCATION,
        dataAttributes: {
            redirect: "/locations/{locationId}/edit"
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: SubscriptionColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.LAST_INVOICE_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.NEXT_INVOICE_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.STATUS,
        dataType: DataType.BADGE,
        dataAttributes: {
            badgeConfigMap: {
                "draft": {
                    message: {
                        id: "generic-listing.invoices.badge.draft",
                        defaultMessage: "Draft",
                    },
                    type: "primary",
                },
                "active": {
                    message: {
                        id: "generic-listing.invoices.badge.active",
                        defaultMessage: "Active",
                    },
                    type: "success",
                },
                "inactive": {
                    message: {
                        id: "generic-listing.invoices.badge.inactive",
                        defaultMessage: "Inactive",
                    },
                    type: "secondary",
                },
                "finished": {
                    message: {
                        id: "generic-listing.invoices.badge.finished",
                        defaultMessage: "Finished",
                    },
                    type: "custom-primary",
                },
            },
            textAlign: "right",
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: SubscriptionColumnIds.TOTAL_AMOUNT,
        dataType: DataType.CURRENCY,
        dataAttributes: {
            textAlign: "right",
        },
        totalAttributes: {
            key: "totals.baseCurrencyTotalAmount",
            type: TotalType.CURRENCY,
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: SubscriptionColumnIds.PO_NUMBER,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: SubscriptionColumnIds.CUSTOMER_TYPE,
        sortId: "contact.customerTypeName",
        accessorFn: row => row.contact?.customerTypeName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.EFFECTIVE_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_NAME,
        sortId: "contact.nameSort",
        accessorFn: row => row.contact?.name,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_FIRST_NAME,
        sortId: "contact.firstNameKeyword",
        accessorFn: row => row.contact?.firstName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_LAST_NAME,
        sortId: "contact.lastNameKeyword",
        accessorFn: row => row.contact?.lastName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_ORGANIZATION_NAME,
        sortId: "contact.organizationNameSort",
        accessorFn: row => row.contact?.organizationName,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_PHONE_NUMBER,
        sortId: "contact.phoneNumberKeyword",
        accessorFn: row => row.contact?.phoneNumber,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CONTACT_EMAIL,
        sortId: "contact.emailKeyword",
        accessorFn: row => row.contact?.email,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SubscriptionColumnIds.CURRENCY,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: SubscriptionColumnIds.TAX_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "taxOptions",
            loadingKey: "loadingTaxOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: SubscriptionColumnIds.PAYMENT_TERM_ID,
        dataType: DataType.OPTIONS,
        dataAttributes: {
            optionsKey: "paymentTermOptions",
            loadingKey: "loadingPaymentTermOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PAYMENT_RELATED],
    },
    {
        id: SubscriptionColumnIds.PRODUCTS_TITLE,
        accessorKey: "rows",
        sortId: "rowsObject.title",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "title",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: SubscriptionColumnIds.PRODUCTS_SKU,
        accessorKey: "rows",
        sortId: "rowsObject.sku",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "sku",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: SubscriptionColumnIds.PRODUCTS_LABELS,
        accessorKey: "rows",
        sortId: "rowsObject.labelIds",
        dataType: DataType.ARRAY,
        dataAttributes: {
            aggregationKey: "labelIds",
            optionsKey: "labelOptions",
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.PRODUCT_RELATED],
    },
    {
        id: SubscriptionColumnIds.SEND_EMAIL,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: SubscriptionColumnIds.NEXT_CREATION_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.END_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.START_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SubscriptionColumnIds.INTERVAL_TYPE,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                [BILLING_METHOD.ONE_TIME]: {
                    id: "generic-listing.billing-type.one-time",
                    defaultMessage: "One Time",
                },
                [BILLING_METHOD.MONTHLY]: {
                    id: "generic-listing.billing-type.monthly",
                    defaultMessage: "Monthly",
                },
                [BILLING_METHOD.YEARLY]: {
                    id: "generic-listing.billing-type.yearly",
                    defaultMessage: "Yearly",
                },
            }
        },
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: SubscriptionColumnIds.CREATED_INVOICE_COUNT,
        dataType: DataType.STRING,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
]

const headerMessages = defineMessages({
    [SubscriptionColumnIds.IDENTIFIER]: {
        id: "generic-listing.subscriptions.table-header.identifier",
        defaultMessage: "ID",
    },
    [SubscriptionColumnIds.LOCATION_NAME]: {
        id: "generic-listing.subscriptions.table-header.location-name",
        defaultMessage: "Location",
    },
    [SubscriptionColumnIds.CONTACT]: {
        id: "generic-listing.subscriptions.table-header.contact",
        defaultMessage: "Contact",
    },
    [SubscriptionColumnIds.LAST_INVOICE_DATE]: {
        id: "generic-listing.subscriptions.table-header.last-invoice-date",
        defaultMessage: "Last Invoice",
    },
    [SubscriptionColumnIds.NEXT_INVOICE_DATE]: {
        id: "generic-listing.subscriptions.table-header.next-invoice-date",
        defaultMessage: "Next Invoice",
    },
    [SubscriptionColumnIds.STATUS]: {
        id: "generic-listing.subscriptions.table-header.status",
        defaultMessage: "Status",
    },
    [SubscriptionColumnIds.TOTAL_AMOUNT]: {
        id: "generic-listing.subscriptions.table-header.total-amount",
        defaultMessage: "Amount",
    },
    [SubscriptionColumnIds.PO_NUMBER]: {
        id: "generic-listing.subscriptions.table-header.po-number",
        defaultMessage: "PO Number",
    },
    [SubscriptionColumnIds.CUSTOMER_TYPE]: {
        id: "generic-listing.subscriptions.table-header.customer-type",
        defaultMessage: "Customer Type",
    },
    [SubscriptionColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.subscriptions.table-header.created-at-date",
        defaultMessage: "Created At Date",
    },
    [SubscriptionColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.subscriptions.table-header.update-at-date",
        defaultMessage: "Updated At Date",
    },
    [SubscriptionColumnIds.EFFECTIVE_DATE]: {
        id: "generic-listing.subscriptions.table-header.effective-date",
        defaultMessage: "Effective Date",
    },
    [SubscriptionColumnIds.CONTACT_NAME]: {
        id: "generic-listing.subscriptions.table-header.contact-name",
        defaultMessage: "Contact Name",
    },
    [SubscriptionColumnIds.CONTACT_FIRST_NAME]: {
        id: "generic-listing.subscriptions.table-header.contact-first-name",
        defaultMessage: "Contact First Name",
    },
    [SubscriptionColumnIds.CONTACT_LAST_NAME]: {
        id: "generic-listing.subscriptions.table-header.contact-last-name",
        defaultMessage: "Contact Last Name",
    },
    [SubscriptionColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.subscriptions.table-header.contact-organization-name",
        defaultMessage: "Contact Organization",
    },
    [SubscriptionColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.subscriptions.table-header.contact-type",
        defaultMessage: "Contact Type",
    },
    [SubscriptionColumnIds.CONTACT_PHONE_NUMBER]: {
        id: "generic-listing.subscriptions.table-header.contact-phone_number",
        defaultMessage: "Contact Phone Number",
    },
    [SubscriptionColumnIds.CONTACT_EMAIL]: {
        id: "generic-listing.subscriptions.table-header.contact-email",
        defaultMessage: "Contact Email",
    },
    [SubscriptionColumnIds.CURRENCY]: {
        id: "generic-listing.subscriptions.table-header.currency",
        defaultMessage: "Currency",
    },
    [SubscriptionColumnIds.TAX_ID]: {
        id: "generic-listing.subscriptions.table-header.tax-id",
        defaultMessage: "Tax",
    },
    [SubscriptionColumnIds.PAYMENT_TERM_ID]: {
        id: "generic-listing.subscriptions.table-header.payment-term-id",
        defaultMessage: "Payment Term",
    },
    [SubscriptionColumnIds.PRODUCTS_TITLE]: {
        id: "generic-listing.subscriptions.table-header.product-title",
        defaultMessage: "Products Title",
    },
    [SubscriptionColumnIds.PRODUCTS_SKU]: {
        id: "generic-listing.subscriptions.table-header.product-sku",
        defaultMessage: "Products SKU",
    },
    [SubscriptionColumnIds.PRODUCTS_LABELS]: {
        id: "generic-listing.subscriptions.table-header.product-labels",
        defaultMessage: "Products Labels",
    },
    [SubscriptionColumnIds.SEND_EMAIL]: {
        id: "generic-listing.subscriptions.table-header.send-email",
        defaultMessage: "Send Email",
    },
    [SubscriptionColumnIds.NEXT_CREATION_DATE]: {
        id: "generic-listing.subscriptions.table-header.next-creation-date",
        defaultMessage: "Next Creation Date",
    },
    [SubscriptionColumnIds.END_DATE]: {
        id: "generic-listing.subscriptions.table-header.end-date",
        defaultMessage: "End Date",
    },
    [SubscriptionColumnIds.START_DATE]: {
        id: "generic-listing.subscriptions.table-header.start-date",
        defaultMessage: "Start Date",
    },
    [SubscriptionColumnIds.INTERVAL_TYPE]: {
        id: "generic-listing.subscriptions.table-header.interval-type",
        defaultMessage: "Interval Type",
    },
    [SubscriptionColumnIds.CREATED_INVOICE_COUNT]: {
        id: "generic-listing.subscriptions.table-header.created-invoice-count",
        defaultMessage: "Created Invoice Count",
    },
})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.SUBSCRIPTIONS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_RECURRING_INVOICES,
    searchPlaceholder: {
        id: "generic-listing.subscriptions.search-placeholder",
        defaultMessage: "Search subscriptions...",
    },
    displayString: {
        id: "generic-listing.subscriptions.display-string",
        defaultMessage: "Subscriptions",
    },
    searchObjectType: ObjectType.INVOICES,
    additionalFilters: {
        billingType: [BILLING_TYPE.RECURRING],
    },
    bulkActionDefinitions: [],
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/SubscriptionCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/subscription.ts